/**
 * ConfirmEmailScreen — saisie du code 6 chiffres reçu par email.
 *
 * Phase 2 (v1.12.118) : flow vérification email obligatoire après signup.
 * Reçoit en paramètre l'email + (optionnel) firstName / lastName.
 * Sur succès → navigate Login (l'user peut se connecter avec ses identifiants).
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  SafeAreaView, View, Text, TextInput, TouchableOpacity,
  StyleSheet, Alert, ActivityIndicator, ScrollView, Platform,
} from 'react-native';
import { useNavigation, useRoute, StackActions } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { showAppAlert } from '../components/AppDialog';
import { rootReplace } from '../utils/rootNavigation';
import { spacing, radius, shadow } from '../theme';
import { useI18n } from '../i18n';

export default function ConfirmEmailScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { confirmEmail, resendConfirmCode, sendEmailVerifyCode, confirmEmailVerifyCode } = useAuth();
  const { theme } = useTheme();
  const { t } = useI18n();

  const email     = route.params?.email     || '';
  const firstName = route.params?.firstName || '';
  const lastName  = route.params?.lastName  || '';
  // v1.12.930 — flow 'verify-email' = pré-auth email verification (login/forgot bloqué par email non vérifié)
  // flow undefined ou 'signup' = signup confirmation classique
  const flow      = route.params?.flow      || 'signup';
  const isVerifyEmailFlow = flow === 'verify-email';

  const [code, setCode]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt]   = useState(null);
  const inputRef = useRef(null);

  // v1.12.931 — Guard ref pour empêcher double send (StrictMode + remount + re-render)
  const autoSentRef = useRef(false);
  useEffect(() => {
    // Focus auto sur l'input au mount
    const t = setTimeout(() => { inputRef.current?.focus?.(); }, 200);
    // v1.12.930 — flow verify-email : envoyer auto le code au mount (UNE SEULE FOIS)
    if (isVerifyEmailFlow && email && !autoSentRef.current) {
      autoSentRef.current = true;
      (async () => {
        const r = await sendEmailVerifyCode({ email });
        if (r?.success && !r?.alreadyVerified) setResentAt(new Date());
      })();
    }
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    const c = (code || '').replace(/\s/g, '');
    if (busy) return;
    if (!email) {
      showAppAlert(t('error_title'), t('email_missing_msg'));
      return;
    }
    if (c.length < 4) {
      showAppAlert(
        t('invalid_code_title'),
        t('invalid_code_msg'),
        [{ text: 'OK', style: 'default' }],
      );
      return;
    }
    setBusy(true);
    // v1.12.930 — flow verify-email utilise les endpoints /auth/email-verify-*
    const r = isVerifyEmailFlow
      ? await confirmEmailVerifyCode({ email, code: c })
      : await confirmEmail({ email, code: c });
    setBusy(false);

    // ─── ÉCHEC : reste sur l'écran, alerte explicite avec choix Renvoyer
    if (!r?.success) {
      const errMsg = (r?.error || '').toLowerCase();
      let title   = t('verify_failed_title');
      let body    = r?.error || t('verify_failed_msg');
      if (errMsg.includes('expir')) {
        title = t('code_expired_title');
        body  = t('code_expired_msg');
      } else if (errMsg.includes('incorrect') || errMsg.includes('mismatch')) {
        title = t('wrong_code_title');
        body  = t('wrong_code_msg');
      }
      showAppAlert(title, body, [
        { text: t('retry_cta'),       style: 'cancel' },
        { text: t('resend_code'),     style: 'default', onPress: onResend },
      ]);
      // Le user reste sur ConfirmEmailScreen pour retaper le code
      return;
    }

    // ─── SUCCÈS : popup confirmation + redirection vers Login ───
    showAppAlert(
      t('account_verified_title'),
      t('account_verified_msg').replace('{email}', email),
      [{ text: t('sign_in_cta'), style: 'default' }],
    ).then(() => {
      // setTimeout 100ms : laisse le popup AppDialog se fermer avant
      // de remplacer le screen courant (sinon parfois ignore sur web).
      setTimeout(() => rootReplace('Login', { prefillEmail: email }), 100);
    });
  };

  const onResend = async () => {
    if (resending || !email) return;
    setResending(true);
    // v1.12.930 — flow verify-email utilise /auth/email-verify-send
    const r = isVerifyEmailFlow
      ? await sendEmailVerifyCode({ email })
      : await resendConfirmCode({ email });
    setResending(false);
    if (r?.success) {
      setResentAt(new Date());
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(t('new_code_sent_msg'));
      } else {
        Alert.alert(t('code_resent_title'), t('code_resent_msg'));
      }
    } else {
      Alert.alert(t('signup_failed_title'), r?.error || t('resend_failed_msg'));
    }
  };

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      <ScrollView contentContainerStyle={s.scroll} keyboardShouldPersistTaps="handled">
        <View style={s.header}>
          <Text style={s.icon}>✉️</Text>
          <Text style={[s.title, { color: theme.text }]}>{t('verify_email_title')}</Text>
          <Text style={[s.subtitle, { color: theme.textFaint }]}>
            {t('verify_email_subtitle')}
          </Text>
          <Text style={[s.email, { color: theme.text }]}>{email || t('verify_email_missing')}</Text>
          <Text style={[s.subtitle, { color: theme.textFaint, marginTop: 6 }]}>
            {t('verify_email_help')}
          </Text>
        </View>

        <View style={s.form}>
          <Text style={[s.label, { color: theme.textFaint }]}>{t('verification_code')}</Text>
          <TextInput
            ref={inputRef}
            value={code}
            onChangeText={(v) => setCode(v.replace(/\D/g, '').slice(0, 8))}
            placeholder="000000"
            placeholderTextColor={theme.textFaint}
            keyboardType="number-pad"
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="oneTimeCode"
            style={[s.codeInput, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }]}
            onSubmitEditing={onSubmit}
            returnKeyType="go"
          />

          <TouchableOpacity
            onPress={onSubmit}
            disabled={busy}
            activeOpacity={0.85}
            style={[s.submitBtn, busy && { opacity: 0.6 }]}
          >
            {busy
              ? <ActivityIndicator color="#fff" size="small" />
              : <Text style={s.submitText}>{t('verify_validate_cta')}</Text>}
          </TouchableOpacity>

          <TouchableOpacity onPress={onResend} disabled={resending} style={s.resendBtn}>
            <Text style={[s.resendText, { color: '#9B7B5A' }]}>
              {resending ? t('resending_code') : t('resend_code')}
            </Text>
          </TouchableOpacity>
          {resentAt && (
            <Text style={[s.resendHint, { color: theme.textFaint }]}>
              {t('code_resent_at').replace('{time}', resentAt.toLocaleTimeString().slice(0, 5))}
            </Text>
          )}

          <TouchableOpacity
            onPress={() => rootReplace('Login', email ? { prefillEmail: email } : undefined)}
            style={s.backBtn}
          >
            <Text style={[s.backText, { color: theme.textFaint }]}>{t('verify_back_link')}</Text>
          </TouchableOpacity>
        </View>

        <Text style={[s.helpText, { color: theme.textFaint }]}>
          {t('verify_email_help_text')}
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1 },
  scroll: { padding: spacing.xl, gap: 20, paddingBottom: 40 },
  header: { alignItems: 'center', gap: 8, marginTop: 24, marginBottom: 8 },
  icon: { fontSize: 40 },
  title: { fontSize: 22, fontWeight: '700', letterSpacing: 0.3, marginTop: 4 },
  subtitle: { fontSize: 13, textAlign: 'center', maxWidth: 320, lineHeight: 18 },
  email: { fontSize: 14, fontWeight: '600', marginTop: 4 },
  form: { gap: 14, marginTop: 8 },
  label: { fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: '600' },
  codeInput: {
    height: 60,
    borderWidth: 1,
    borderRadius: radius.md,
    paddingHorizontal: 16,
    fontSize: 24,
    letterSpacing: 8,
    textAlign: 'center',
    fontWeight: '600',
  },
  submitBtn: {
    height: 50,
    borderRadius: radius.md,
    backgroundColor: '#9B7B5A',
    alignItems: 'center', justifyContent: 'center',
    ...shadow.sm,
  },
  submitText: { color: '#fff', fontSize: 14, fontWeight: '700', letterSpacing: 1 },
  resendBtn: { alignSelf: 'center', paddingVertical: 8 },
  resendText: { fontSize: 13, fontWeight: '600', letterSpacing: 0.3 },
  resendHint: { fontSize: 11, textAlign: 'center', marginTop: -4 },
  backBtn: { alignSelf: 'center', paddingVertical: 12 },
  backText: { fontSize: 13 },
  helpText: { fontSize: 11, textAlign: 'center', marginTop: 16, paddingHorizontal: 16, lineHeight: 16 },
});
